/**
 * Page /admin/modules — activation des briques (modules) de la commune.
 *
 * Pilotage réservé super_admin + admin (maire). Modèle opt-out : tout est actif
 * par défaut. Les modules SOCLE (Relation citoyen, Service technique) ne sont
 * jamais désactivables. Masquage purement cosmétique côté usager.
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, Badge, Alert, LogoMark, SectionEyebrow } from '@/components/ui';
import { api } from '@/lib/api';
import { useModules } from '@/lib/modules-context';
import {
  LayoutGrid, AlertCircle, Lock, GraduationCap, Users, Building2, Calculator, Baby, Megaphone, Hammer,
  MapPin, Bell, Boxes, UserRound,
} from 'lucide-react';

/** Métadonnées d'affichage par clé de module. */
const MODULE_META: Record<string, { label: string; description: string; icon: typeof Users }> = {
  service_technique: { label: 'Service technique', description: 'Voirie · espaces verts · bâtiments · matériel', icon: Hammer },
  grc: { label: 'Relation citoyen (GRC)', description: 'Accueil · alertes · communication', icon: Megaphone },
  scolaire_cantine: { label: 'Scolaire & cantine', description: 'Inscriptions · menus · périscolaire · facturation', icon: GraduationCap },
  associations: { label: 'Association / Acteurs locaux', description: 'Annuaire · commerces & producteurs · subventions · salles', icon: Users },
  urbanisme: { label: 'Urbanisme', description: 'Permis · déclarations · certificats', icon: Building2 },
  comptable: { label: 'Comptabilité', description: 'Encaissements · bordereaux · journal', icon: Calculator },
  petite_enfance: { label: 'Petite enfance', description: 'Crèche · RAM · contrats · pré-inscriptions', icon: Baby },
  agents: { label: 'Mon dossier personnel', description: 'Liens utiles · annuaire des numéros d\'urgence (PCS)', icon: UserRound },
};

/** Métadonnées des briques pilotables côté ADMINISTRÉ (espace citoyen). */
const VIS_META: Record<string, { label: string; description: string; icon: typeof Users }> = {
  mes_signalements: { label: 'Signalements', description: 'Le citoyen déclare un incident sur la voie publique', icon: MapPin },
  mes_alertes: { label: 'Alertes & communication', description: 'Alertes municipales, newsletter, notifications', icon: Bell },
  location_salles_materiel: { label: 'Location salles / matériel', description: 'Réservation de salles et de matériel par le citoyen', icon: Boxes },
  associations: { label: 'Association / Acteurs locaux', description: 'Annuaire, adhésions, commerces & producteurs côté citoyen', icon: Users },
  enfance: { label: 'Enfance', description: 'Scolaire, cantine, ALSH et petite enfance (espace famille)', icon: GraduationCap },
  urbanisme: { label: 'Urbanisme', description: 'Dépôt de demandes d\'urbanisme en ligne', icon: Building2 },
};

interface ModuleEtat { moduleKey: string; actif: boolean; socle: boolean }
interface VisEtat { key: string; visible: boolean }

export function TenantModulesPage() {
  const { refresh: refreshGlobalModules } = useModules();
  const [modules, setModules] = useState<ModuleEtat[]>([]);
  const [visibilite, setVisibilite] = useState<VisEtat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [mods, vis] = await Promise.all([
        api.admin.modules.list(),
        api.admin.modules.listVisibiliteAdministre(),
      ]);
      setModules(mods.modules);
      setVisibilite(vis.visibilite);
    }
    catch { setError('Impossible de charger les modules.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const toggle = async (m: ModuleEtat) => {
    if (m.socle) return;
    setSaving(m.moduleKey); setError(null);
    try {
      await api.admin.modules.set(m.moduleKey, !m.actif);
      await load();
      await refreshGlobalModules(); // met à jour tuiles/menus immédiatement
    } catch { setError('Modification impossible.'); }
    finally { setSaving(null); }
  };

  const toggleVis = async (v: VisEtat) => {
    setSaving(`vis:${v.key}`); setError(null);
    try {
      await api.admin.modules.setVisibiliteAdministre(v.key, !v.visible);
      await load();
      await refreshGlobalModules();
    } catch { setError('Modification impossible.'); }
    finally { setSaving(null); }
  };

  // Socle d'abord, puis optionnels
  const socle = modules.filter((m) => m.socle);
  const optionnels = modules.filter((m) => !m.socle);

  return (
    <div className="text-ink">
      <div className="mb-5 flex items-center gap-3">
        <LogoMark icon={LayoutGrid} accent="tech" size="lg" />
        <div>
          <SectionEyebrow as="div">Administration</SectionEyebrow>
          <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">Modules de la commune</h1>
        </div>
      </div>

      <p className="mb-4 max-w-2xl text-sm text-ink-muted">
        Activez ou désactivez les briques selon les services rendus par votre commune.
        Une brique désactivée n'apparaît plus pour les agents ni les administrés — c'est purement
        un confort d'affichage, vos données restent protégées. Tout est actif par défaut.
      </p>

      {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}

      {loading ? (
        <Card variant="glass" padded><div className="py-10 text-center text-sm text-ink-subtle">Chargement…</div></Card>
      ) : (
        <div className="space-y-5">
          {/* Modules optionnels (pilotables) */}
          <section>
            <div className="mb-2 font-display text-sm font-semibold text-ink">Modules optionnels</div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {optionnels.map((m) => {
                const meta = MODULE_META[m.moduleKey] ?? { label: m.moduleKey, description: '', icon: LayoutGrid };
                const Icon = meta.icon;
                return (
                  <Card key={m.moduleKey} variant="glass" className={`flex items-center gap-3 p-3.5 ${m.actif ? '' : 'opacity-70'}`}>
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-r-xl border border-border bg-surface/50">
                      <Icon className="h-5 w-5 text-ink-muted" aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-ink">{meta.label}</div>
                      <div className="text-[11px] text-ink-subtle">{meta.description}</div>
                    </div>
                    {/* Toggle */}
                    <button
                      type="button"
                      role="switch"
                      aria-checked={m.actif}
                      aria-label={`${m.actif ? 'Désactiver' : 'Activer'} ${meta.label}`}
                      onClick={() => void toggle(m)}
                      disabled={saving === m.moduleKey}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full border transition-colors ${m.actif ? 'border-success/60 bg-success/70' : 'border-border bg-surface-hi ring-1 ring-inset ring-border'} disabled:opacity-50`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-soft transition-transform ${m.actif ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Visibilité côté administré (espace citoyen) */}
          <section>
            <div className="mb-1 font-display text-sm font-semibold text-ink">Visible par les administrés</div>
            <p className="mb-2 max-w-2xl text-[11px] text-ink-subtle">
              Choisissez ce qui apparaît dans l'espace citoyen, indépendamment de l'usage interne.
              Exemple : la location de salles/matériel est gérée par le service technique, mais vous
              pouvez la proposer (ou non) au public.
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {visibilite.map((v) => {
                const meta = VIS_META[v.key] ?? { label: v.key, description: '', icon: LayoutGrid };
                const Icon = meta.icon;
                return (
                  <Card key={v.key} variant="glass" className={`flex items-center gap-3 p-3.5 ${v.visible ? '' : 'opacity-70'}`}>
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-r-xl border border-border bg-surface/50">
                      <Icon className="h-5 w-5 text-ink-muted" aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-ink">{meta.label}</div>
                      <div className="text-[11px] text-ink-subtle">{meta.description}</div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={v.visible}
                      aria-label={`${v.visible ? 'Masquer' : 'Afficher'} ${meta.label} côté administré`}
                      onClick={() => void toggleVis(v)}
                      disabled={saving === `vis:${v.key}`}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full border transition-colors ${v.visible ? 'border-success/60 bg-success/70' : 'border-border bg-surface-hi ring-1 ring-inset ring-border'} disabled:opacity-50`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-soft transition-transform ${v.visible ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Modules socle (verrouillés) */}
          <section>
            <div className="mb-2 font-display text-sm font-semibold text-ink">Socle (toujours actif)</div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {socle.map((m) => {
                const meta = MODULE_META[m.moduleKey] ?? { label: m.moduleKey, description: '', icon: LayoutGrid };
                const Icon = meta.icon;
                return (
                  <Card key={m.moduleKey} variant="glass" className="flex items-center gap-3 p-3.5">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-r-xl border border-border bg-surface/50">
                      <Icon className="h-5 w-5 text-ink-muted" aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-ink">{meta.label}</div>
                      <div className="text-[11px] text-ink-subtle">{meta.description}</div>
                    </div>
                    <Badge variant="neutral" size="sm"><Lock className="mr-1 h-3 w-3" aria-hidden="true" />Socle</Badge>
                  </Card>
                );
              })}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
